/**
 * Admin Sectors Tab (PR #5g).
 *
 * Reads from GET /admin/sectors and provides full CRUD via the sector
 * API endpoints. Requires `admin.sectors.manage`.
 *
 * Each sector's default risk class is displayed as a coloured badge.
 * The modal form is a controlled component; slug is derived server-side
 * from the name on creation and preserved on updates.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import type { SectorDto, UpsertSectorBody } from '@cortex/domain';
import { SectorRiskClassSchema } from '@cortex/domain';

import { cortexApi, isApiConfigured } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { FormField } from '@/components/ui/FormField';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge, type StatusBadgeTone } from '@/components/ui/StatusBadge';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@/components/ui/DataTable';

// ─── helpers ──────────────────────────────────────────────────────────────────

const RISK_TONES: Record<string, StatusBadgeTone> = {
  'Risk 1 (Low)': 'active',
  'Risk 2 (Medium)': 'warning',
  'Risk 3 (High)': 'warning',
  'Risk 4 (Extreme)': 'suspended',
};

const riskTone = (risk: string): StatusBadgeTone => RISK_TONES[risk] ?? 'neutral';

// ─── modal ────────────────────────────────────────────────────────────────────

interface SectorFormData {
  name: string;
  description: string;
  longDescription: string;
  defaultRisk: string;
  drivingRisk: boolean;
  displayOrder: number;
}

const emptyForm = (): SectorFormData => ({
  name: '',
  description: '',
  longDescription: '',
  defaultRisk: 'Risk 1 (Low)',
  drivingRisk: false,
  displayOrder: 0,
});

const fromSector = (s: SectorDto): SectorFormData => ({
  name: s.name,
  description: s.description,
  longDescription: s.longDescription,
  defaultRisk: s.defaultRisk,
  drivingRisk: s.drivingRisk,
  displayOrder: s.displayOrder,
});

interface SectorModalProps {
  editing: SectorDto | null;
  onClose: () => void;
  onSave: (id: string | undefined, body: UpsertSectorBody) => void;
  saving: boolean;
}

const SectorModal: React.FC<SectorModalProps> = ({ editing, onClose, onSave, saving }) => {
  const [form, setForm] = useState<SectorFormData>(editing ? fromSector(editing) : emptyForm());

  const handleSubmit = () => {
    const parsed = SectorRiskClassSchema.safeParse(form.defaultRisk);
    if (!parsed.success) return;
    onSave(editing?.id, {
      name: form.name.trim(),
      description: form.description.trim(),
      longDescription: form.longDescription.trim(),
      defaultRisk: parsed.data,
      drivingRisk: form.drivingRisk,
      displayOrder: form.displayOrder,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
        <div className="bg-hr-indigo p-4 border-b border-slate-800">
          <h3 className="text-white font-bold text-sm">
            {editing ? 'Edit Sector' : 'Add New Sector'}
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <FormField label="Sector Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Sector 15: Space Travel"
            />
          </FormField>
          <FormField label="Short Description (dropdown)">
            <textarea
              className="w-full p-2 border border-slate-200 rounded text-xs h-14 focus:outline-none focus:ring-1 focus:ring-hr-indigo"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Banking, insurance, asset management (Office Based)"
            />
          </FormField>
          <FormField label="Long Description (more info)">
            <textarea
              className="w-full p-2 border border-slate-200 rounded text-xs h-24 focus:outline-none focus:ring-1 focus:ring-hr-indigo"
              value={form.longDescription}
              onChange={(e) => setForm({ ...form, longDescription: e.target.value })}
              placeholder="Full risk-profile narrative shown under 'More info'…"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Default Risk Class">
              <select
                className="w-full p-2 border border-slate-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-hr-indigo"
                value={form.defaultRisk}
                onChange={(e) => setForm({ ...form, defaultRisk: e.target.value })}
              >
                {SectorRiskClassSchema.options.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Display Order">
              <Input
                type="number"
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value) || 0 })}
              />
            </FormField>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 text-hr-pink focus:ring-hr-pink rounded"
              checked={form.drivingRisk}
              onChange={(e) => setForm({ ...form, drivingRisk: e.target.checked })}
            />
            <span className="text-xs font-medium text-slate-700">Potential Driving Exposure</span>
          </label>
        </div>
        <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-end gap-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={saving || form.name.trim().length === 0}
            className="bg-hr-pink hover:bg-hr-pink-hover text-white"
          >
            {saving ? 'Saving…' : 'Save Sector'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

export const SectorsTab: React.FC = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SectorDto | null | 'new'>(null);

  const sectorsQuery = useQuery({
    queryKey: ['admin', 'sectors'],
    queryFn: () => cortexApi.sectors.list(),
    enabled: isApiConfigured(),
  });

  const upsertMutation = useMutation({
    mutationFn: ({ id, body }: { id: string | undefined; body: UpsertSectorBody }) =>
      cortexApi.sectors.upsert(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sectors'] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => cortexApi.sectors.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sectors'] });
    },
  });

  const handleSave = (id: string | undefined, body: UpsertSectorBody) => {
    upsertMutation.mutate({ id, body });
  };

  if (!isApiConfigured()) {
    return (
      <EmptyState
        icon={Layers}
        title="API not configured"
        description="Set VITE_API_BASE_URL to connect to the backend."
      />
    );
  }

  const errorMessage = sectorsQuery.error
    ? resolveApiError(sectorsQuery.error).message
    : upsertMutation.error
      ? resolveApiError(upsertMutation.error).message
      : deleteMutation.error
        ? resolveApiError(deleteMutation.error).message
        : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Industry Sectors"
        toolbar={
          <Button
            size="sm"
            onClick={() => setEditing('new')}
            className="bg-hr-pink hover:bg-hr-pink-hover text-white text-xs font-bold"
          >
            + Add Sector
          </Button>
        }
      />

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded px-4 py-3">
          {errorMessage}
        </div>
      )}

      <div className="rounded-lg shadow-sm border border-border bg-card overflow-hidden">
        <DataTable>
          <DataTableHead>
            <DataTableHeader className="px-6 py-3">Sector Name</DataTableHeader>
            <DataTableHeader className="px-6 py-3">Default Risk</DataTableHeader>
            <DataTableHeader className="px-6 py-3">Description</DataTableHeader>
            <DataTableHeader className="px-6 py-3">Driving</DataTableHeader>
            <DataTableHeader align="right" className="px-6 py-3">
              Actions
            </DataTableHeader>
          </DataTableHead>
          <DataTableBody>
            {sectorsQuery.isPending &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-6 py-4">
                    <Skeleton className="h-3 w-48" />
                  </td>
                  <td className="px-6 py-4">
                    <Skeleton className="h-3 w-24" />
                  </td>
                  <td className="px-6 py-4">
                    <Skeleton className="h-3 w-40" />
                  </td>
                  <td className="px-6 py-4">
                    <Skeleton className="h-3 w-8" />
                  </td>
                  <td className="px-6 py-4" />
                </tr>
              ))}

            {sectorsQuery.isSuccess && sectorsQuery.data.data.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <EmptyState
                    icon={Layers}
                    title="No sectors defined"
                    description="Add a sector to allow risk classification in quotes."
                  />
                </td>
              </tr>
            )}

            {sectorsQuery.isSuccess &&
              sectorsQuery.data.data.map((sector) => (
                <DataTableRow key={sector.id}>
                  <td className="px-6 py-4 font-bold text-foreground">{sector.name}</td>
                  <td className="px-6 py-4">
                    <StatusBadge tone={riskTone(sector.defaultRisk)} label={sector.defaultRisk} />
                  </td>
                  <td className="px-6 py-4 text-muted-foreground text-micro max-w-xs truncate">
                    {sector.description}
                  </td>
                  <td className="px-6 py-4">
                    {sector.drivingRisk && (
                      <span className="bg-foreground text-background px-2 py-0.5 rounded text-micro font-bold uppercase">
                        Yes
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditing(sector)}
                        className="text-muted-foreground hover:text-hr-indigo font-bold text-micro"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(sector.id)}
                        disabled={deleteMutation.isPending}
                        className="text-destructive/40 hover:text-destructive font-bold text-micro"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </DataTableRow>
              ))}
          </DataTableBody>
        </DataTable>
      </div>

      {editing !== null && (
        <SectorModal
          editing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={upsertMutation.isPending}
        />
      )}
    </div>
  );
};
